import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";
import { BeatLoader } from "react-spinners";
import ModalDialog from "./ModalDialog";

interface ConfirmationModalProps {
  isOpen: boolean;
  confirmationMessage: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  /** The dismiss button's label; omit to keep "Cancel". */
  cancelLabel?: string;
  saving?: boolean;
  /** Overrides the default "Confirm" header; omit to keep today's rendering. */
  title?: string;
  /** Rendered left of the title when provided; omitted entirely otherwise. */
  icon?: React.ReactNode;
  /** When provided, renders a keycap badge (e.g. "B") inside the confirm button. */
  confirmHotkeyLabel?: string;
  /** Replaces the panel's default `max-w-md` sizing (e.g. `max-w-sm` for a narrower dialog); omit to keep the default width. */
  className?: string;
  /** Overrides the confirm button's classes (a `relative` class for the saving-spinner overlay is always prepended regardless); takes precedence over `danger`. Omit to keep the default primary-button look. */
  confirmButtonClassName?: string;
  /** Decorative glyph rendered before the confirm label, marked `aria-hidden` so it's never read as part of the button's accessible name; omit to render no icon. */
  confirmIcon?: string;
  /** Destructive treatment: primary-orange icon badge beside the title and an orange confirm button (the app-wide destructive color). `confirmButtonClassName` still wins when both are provided. */
  danger?: boolean;
  /**
   * Extra content rendered between the message and the buttons — e.g. an opt-in
   * checkbox the confirmation itself carries (AKL-753's "Also set photos to
   * Reviewed"). Omit to render nothing, which is what every existing caller does.
   *
   * Deliberately a slot rather than more props: what a confirmation needs to ask
   * alongside its message is caller-specific, and growing this component a field
   * per case would make it everyone's problem.
   */
  extraContent?: React.ReactNode;
  /** Passed to `ModalDialog`: where focus goes on close when nothing had focus as this opened. Omit to keep today's behaviour. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  confirmationMessage,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  saving,
  title,
  icon,
  confirmHotkeyLabel,
  className,
  confirmButtonClassName,
  confirmIcon,
  danger,
  extraContent,
  returnFocusRef,
}) => {
  const { t } = useTranslation();
  // A generated id, not a fixed one: several pages mount more than one of these.
  const titleId = useId();

  if (!isOpen) return null;

  const confirmClasses =
    confirmButtonClassName ??
    (danger ? tailwindClassnames.btnDanger : tailwindClassnames.btnPrimary);

  return (
    <ModalDialog
      onClose={onCancel}
      busy={saving}
      labelledBy={titleId}
      overlayClassName="z-50"
      panelClassName={className ?? "max-w-md"}
      returnFocusRef={returnFocusRef}
    >
      <>
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            {danger && !icon && (
              <span
                aria-hidden="true"
                className={tailwindClassnames.modalDangerIcon}
              >
                !
              </span>
            )}
            {icon}
            <h2 id={titleId} className={tailwindClassnames.modalTitle}>
              {title ?? t("Confirm")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onCancel}
            disabled={saving}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>
        <p className={tailwindClassnames.modalBody}>{confirmationMessage}</p>
        {/* Same body container as the message, so injected content lines up with
            it instead of sitting flush against the panel edge. Its own element
            rather than wrapping both, so the message's rendering is untouched
            for every existing caller. `pt-0` because the message above already
            supplies the gap. */}
        {extraContent && (
          <div className={`${tailwindClassnames.modalBody} pt-0`}>
            {extraContent}
          </div>
        )}
        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onCancel}
            disabled={saving}
          >
            {cancelLabel ?? t("Cancel")}
          </button>
          <button
            type="button"
            className={`relative ${confirmClasses}`}
            onClick={onConfirm}
            disabled={saving}
            aria-keyshortcuts={confirmHotkeyLabel?.toLowerCase()}
          >
            {/* Keep the label in layout while saving so the button keeps its
                width (no jump on confirm); the spinner uses currentColor so it
                reads on any button fill (primary, danger, or a custom
                confirmButtonClassName) in both themes. */}
            <span
              className={`inline-flex items-center gap-2 ${
                saving ? "invisible" : ""
              }`}
            >
              {confirmIcon && <span aria-hidden="true">{confirmIcon}</span>}
              {confirmLabel ?? t("Yes")}
              {confirmHotkeyLabel && (
                <kbd
                  aria-hidden="true"
                  className="rounded bg-black/20 px-1.5 py-0.5 text-[10px] font-mono"
                >
                  {confirmHotkeyLabel}
                </kbd>
              )}
            </span>
            {saving && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </>
    </ModalDialog>
  );
};

export default ConfirmationModal;
